import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/__diag/en-aliases")({
  head: () => ({
    meta: [
      { title: "Internal QA - EN Aliases" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: DiagPage,
});

const ALIASES: Array<{ source: string; expected: string; marker?: string }> = [
  { source: "/en/cuti-bersama/2026", expected: "/en/joint-leave/2026", marker: "Joint Leave" },
  { source: "/en/tanggal-merah/2026", expected: "/en/red-dates/2026", marker: "Red Dates" },
  { source: "/en/libur-nasional/2026", expected: "/en/public-holidays/2026", marker: "Public Holidays" },
  { source: "/en/kalender-hijriah/1447", expected: "/en/hijri-calendar/1447", marker: "Hijri" },
  { source: "/en/kalender-hijriah/1447/9", expected: "/en/hijri-calendar/1447/9", marker: "Hijri" },
  { source: "/en/hari-ini", expected: "/en/today", marker: "" },
  { source: "/en/tentang", expected: "/en/about", marker: "About" },
  { source: "/en/tools/kalkulator-hari", expected: "/en/tools/day-calculator", marker: "" },
  { source: "/en/tools/cek-weton", expected: "/en/tools/weton-check", marker: "" },
];

type Status =
  | { kind: "pending" }
  | { kind: "ok"; finalUrl: string; markerFound: boolean }
  | { kind: "fail"; reason: string; finalUrl?: string };

function DiagPage() {
  const [results, setResults] = useState<Record<string, Status>>(
    Object.fromEntries(ALIASES.map((a) => [a.source, { kind: "pending" } as Status])),
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const a of ALIASES) {
        try {
          const res = await fetch(a.source, { redirect: "follow" });
          const finalUrl = new URL(res.url).pathname;
          if (res.status === 404) {
            if (!cancelled) setResults((r) => ({ ...r, [a.source]: { kind: "fail", reason: "404", finalUrl } }));
            continue;
          }
          const text = await res.text();
          const markerFound = a.marker ? text.includes(a.marker) : true;
          const ok = finalUrl === a.expected;
          if (!cancelled) {
            setResults((r) => ({
              ...r,
              [a.source]: ok
                ? { kind: "ok", finalUrl, markerFound }
                : { kind: "fail", reason: `landed on ${finalUrl}, expected ${a.expected}`, finalUrl },
            }));
          }
        } catch (e) {
          if (!cancelled) {
            setResults((r) => ({ ...r, [a.source]: { kind: "fail", reason: String(e) } }));
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto max-w-3xl p-6">
      <header className="mb-4">
        <h1 className="text-2xl font-bold">Internal QA: EN slug aliases</h1>
        <p className="text-sm text-muted-foreground">
          Not indexed. Verifies that legacy Indonesian slugs under /en/ redirect to translated paths.
        </p>
      </header>
      <div className="overflow-hidden rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2">Expected</th>
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {ALIASES.map((a) => {
              const s = results[a.source];
              return (
                <tr key={a.source} className="border-t border-border">
                  <td className="px-3 py-2 font-mono text-xs">{a.source}</td>
                  <td className="px-3 py-2 font-mono text-xs">{a.expected}</td>
                  <td className="px-3 py-2">
                    {s.kind === "pending" && <span className="text-muted-foreground">…</span>}
                    {s.kind === "ok" && (
                      <span className="text-green-600">
                        ✅ OK{!s.markerFound && " (marker missing)"}
                      </span>
                    )}
                    {s.kind === "fail" && (
                      <span className="text-red-600">❌ {s.reason}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}